/**
 * React intl integration.
 *
 * The provider holds the `IntlShape` in React context, so two trees rendering
 * with different locales (the concurrent-SSR hazard that the old module-level
 * `intl` singleton had) cannot bleed into each other.
 */

import * as React from "react";
import {
  FormattedDate,
  FormattedDateParts,
  FormattedDateTimeRange,
  FormattedDisplayName,
  FormattedList,
  FormattedListParts,
  FormattedMessage,
  FormattedNumber,
  FormattedNumberParts,
  FormattedPlural,
  FormattedRelativeTime,
  FormattedTime,
  FormattedTimeParts,
  IntlProvider as ReactIntlProvider,
  RawIntlProvider,
  injectIntl,
  useIntl as useReactIntl,
  type IntlConfig,
  type IntlShape,
  type MessageDescriptor,
} from "react-intl";

import {
  getIntl,
  getMessages,
  handleIntlError,
  type TVocabulary,
} from "./intl";
import { DEFAULT_LOCALE, normalizeLocale, type TLocale } from "./locales";

export type TIntlProviderProps = Omit<
  React.ComponentProps<typeof ReactIntlProvider>,
  "locale" | "messages" | "defaultLocale"
> & {
  /**
   * Locale for this tree. Accepts the legacy spellings (`en-US`, `en_US`) and
   * anything unrecognized resolves to `en`.
   */
  locale?: TLocale | string | null;
  /** Per-request catalog. Overrides both the bundled locale catalog and `en`. */
  messages?: TVocabulary;
};

/**
 * Intl provider for a React tree.
 *
 * Always merges the English catalog underneath, so a key missing from a
 * translation renders its canonical English message instead of the raw id.
 * Use it once per SSG/SSR render with the request's locale.
 */
export function IntlProvider({
  locale,
  messages,
  onError,
  ...rest
}: TIntlProviderProps): React.ReactElement {
  const resolved = normalizeLocale(locale);

  return (
    <ReactIntlProvider
      {...rest}
      locale={resolved}
      defaultLocale={DEFAULT_LOCALE}
      messages={getMessages(resolved, messages)}
      onError={onError ?? handleIntlError}
    />
  );
}

export {
  FormattedDate,
  FormattedDateParts,
  FormattedDateTimeRange,
  FormattedDisplayName,
  FormattedList,
  FormattedListParts,
  FormattedMessage,
  FormattedNumber,
  FormattedNumberParts,
  FormattedPlural,
  FormattedRelativeTime,
  FormattedTime,
  FormattedTimeParts,
  RawIntlProvider,
  injectIntl,
};

let warnedAboutMissingProvider = false;

/**
 * Warns once, in development only.
 *
 * A library that quietly swallows a missing provider hides a real mistake, so the
 * first unprovided tree names itself. Only the first: a component rendered in a
 * list would otherwise print the same line hundreds of times.
 */
function warnAboutMissingProvider(): void {
  if (warnedAboutMissingProvider) return;
  warnedAboutMissingProvider = true;
  if (process.env.NODE_ENV === "production") return;
  // eslint-disable-next-line no-console
  console.warn(
    "[@atlas/text] useIntl() outside an <IntlProvider>: falling back to English. " +
      "Mount <IntlProvider> (or your app's locale provider) above this tree."
  );
}

/**
 * `useIntl()` that never throws.
 *
 * react-intl requires an `<IntlProvider>` ancestor and throws during render
 * without one. That is the right invariant for app code and the wrong one for a
 * shared component library: `packages/uicorn` is rendered by surfaces that have no
 * provider and no intention of having one - the customer-facing widget in
 * `client-js` renders `AttachmentPreview`, which renders an icon whose
 * `aria-label` comes from a message. A label lookup must not be able to break a
 * customer's page, so a tree without a provider falls back to the default locale:
 * exactly the English the widget rendered before the strings were extracted.
 *
 * The fallback is the memoized `IntlShape` that `t()` uses, so it also follows
 * `setupLocale()` / `loadCatalog()` in a host that never mounts a provider.
 */
export function useIntl(): IntlShape {
  try {
    return useReactIntl();
  } catch {
    warnAboutMissingProvider();

    return getIntl(DEFAULT_LOCALE);
  }
}

/**
 * `defineMessage` / `defineMessages` that keep the id as a literal type.
 *
 * react-intl's own overloads infer `id: string`, which throws away the one piece
 * of information the non-React entry point needs: `t()` takes the union of ids in
 * the catalog, so `t(copy.save.id)` type-checks - and a typo'd id does not - only
 * while the literal survives into the descriptor. App code imports these, not
 * react-intl's, so the ids stay checkable end to end. The inferred descriptors
 * are readonly, which react-intl accepts everywhere it takes a `MessageDescriptor`.
 */
export function defineMessage<const T extends MessageDescriptor>(
  message: T
): T {
  return message;
}

export function defineMessages<
  const T extends Record<string, MessageDescriptor>
>(messages: T): T {
  return messages;
}

export type { IntlConfig, IntlShape, MessageDescriptor };
